/**
 * Feature flag system for environment-specific features
 * Allows features to be enabled/disabled based on environment, configuration, or runtime toggles
 */

import { getEnvironment, type Environment } from "./environment";

/**
 * Feature flag configuration
 */
export interface FeatureFlagConfig {
  /** Feature name */
  name: string;
  /** Default value if no override is found */
  defaultValue: boolean;
  /** Environment-specific overrides */
  environmentOverrides?: Partial<Record<Environment, boolean>>;
  /** Development mode override */
  developmentOverride?: boolean;
  /** Production mode override */
  productionOverride?: boolean;
  /** Description of what this feature does */
  description?: string;
}

/**
 * Runtime feature flag overrides (for testing/debugging)
 */
const runtimeOverrides = new Map<string, boolean>();

/**
 * Default feature flag configurations
 */
const defaultFeatureFlags: Record<string, FeatureFlagConfig> = {
  // Navigation features
  "navigation.electron-specific": {
    name: "navigation.electron-specific",
    defaultValue: false,
    environmentOverrides: {
      electron: true,
    },
    description: "Show Electron-specific navigation items",
  },
  "navigation.web-specific": {
    name: "navigation.web-specific",
    defaultValue: false,
    environmentOverrides: {
      web: true,
    },
    description: "Show web-specific navigation items",
  },

  // UI features
  "ui.native-menus": {
    name: "ui.native-menus",
    defaultValue: false,
    environmentOverrides: {
      electron: true,
    },
    description: "Enable native application menus",
  },
  "ui.context-menus": {
    name: "ui.context-menus",
    defaultValue: false,
    environmentOverrides: {
      electron: true,
    },
    description: "Enable native context menus",
  },
  "ui.drag-and-drop": {
    name: "ui.drag-and-drop",
    defaultValue: false,
    environmentOverrides: {
      electron: true,
    },
    description: "Enable drag and drop functionality",
  },
  "ui.keyboard-shortcuts": {
    name: "ui.keyboard-shortcuts",
    defaultValue: false,
    environmentOverrides: {
      electron: true,
    },
    description: "Enable keyboard shortcuts",
  },

  // Development features
  "dev.devtools": {
    name: "dev.devtools",
    defaultValue: false,
    developmentOverride: true,
    description: "Enable developer tools",
  },
  "dev.hot-reload": {
    name: "dev.hot-reload",
    defaultValue: false,
    developmentOverride: true,
    description: "Enable hot reload functionality",
  },
  "dev.debug-logging": {
    name: "dev.debug-logging",
    defaultValue: false,
    developmentOverride: true,
    description: "Enable debug logging",
  },

  // Authentication features
  "auth.2fa": {
    name: "auth.2fa",
    defaultValue: true,
    description: "Enable two-factor authentication",
  },
  "auth.remember-me": {
    name: "auth.remember-me",
    defaultValue: true,
    description: "Enable remember me functionality",
  },

  // Experimental features
  "experimental.new-dashboard": {
    name: "experimental.new-dashboard",
    defaultValue: false,
    developmentOverride: true,
    description: "Enable experimental new dashboard",
  },
  "experimental.advanced-search": {
    name: "experimental.advanced-search",
    defaultValue: false,
    description: "Enable experimental advanced search",
  },

  // The Experimental → Experiments nav group and the /experiments route tree.
  //
  // On everywhere (OTO-187). It shipped dark under OTO-172, but "dark" resolved
  // through getIsDevelopment(), which is true only for a localhost dev server —
  // so the group was invisible on staging and production with no supported way
  // to reveal it: setFeatureFlagOverride() is never called anywhere, and its
  // Map does not survive a reload. The flag stays declared so the nav entry and
  // the /experiments route guard keep a single switch to read.
  experiments_gallery: {
    name: "experiments_gallery",
    defaultValue: true,
    description: "Show the Experiments navigation group and its routes",
  },
};

/**
 * Get the value of a feature flag
 * @param flagName - The name of the feature flag
 * @param config - Optional custom configuration for the flag
 * @returns The resolved boolean value of the feature flag
 */
export function getFeatureFlag(flagName: string, config?: FeatureFlagConfig): boolean {
  // Check runtime override first (highest priority)
  if (runtimeOverrides.has(flagName)) {
    return runtimeOverrides.get(flagName)!;
  }

  // Get configuration (use provided config or default)
  const flagConfig = config || defaultFeatureFlags[flagName];
  if (!flagConfig) {
    console.warn(`Feature flag "${flagName}" not found. Using default value: false`);
    return false;
  }

  const environment = getEnvironment();

  // Check environment-specific override
  if (
    flagConfig.environmentOverrides &&
    environment.environment in flagConfig.environmentOverrides
  ) {
    return flagConfig.environmentOverrides[environment.environment]!;
  }

  // Check development/production overrides
  if (environment.isDevelopment && flagConfig.developmentOverride !== undefined) {
    return flagConfig.developmentOverride;
  }

  if (environment.isProduction && flagConfig.productionOverride !== undefined) {
    return flagConfig.productionOverride;
  }

  // Return default value
  return flagConfig.defaultValue;
}

/**
 * Set a runtime override for a feature flag (for testing/debugging)
 * @param flagName - The name of the feature flag
 * @param value - The override value
 */
export function setFeatureFlagOverride(flagName: string, value: boolean): void {
  runtimeOverrides.set(flagName, value);
}

/**
 * Remove a runtime override for a feature flag
 * @param flagName - The name of the feature flag
 */
export function clearFeatureFlagOverride(flagName: string): void {
  runtimeOverrides.delete(flagName);
}

/**
 * Clear all runtime overrides
 */
export function clearAllFeatureFlagOverrides(): void {
  runtimeOverrides.clear();
}

/**
 * Get all current runtime overrides
 * @returns Map of flag names to override values
 */
export function getFeatureFlagOverrides(): Map<string, boolean> {
  return new Map(runtimeOverrides);
}

/**
 * Check if a feature flag has a runtime override
 * @param flagName - The name of the feature flag
 * @returns true if the flag has a runtime override
 */
export function hasFeatureFlagOverride(flagName: string): boolean {
  return runtimeOverrides.has(flagName);
}

/**
 * Get all available feature flags with their current values
 * @returns Object with flag names as keys and their resolved values
 */
export function getAllFeatureFlags(): Record<string, boolean> {
  const result: Record<string, boolean> = {};

  for (const flagName of Object.keys(defaultFeatureFlags)) {
    result[flagName] = getFeatureFlag(flagName);
  }

  return result;
}

/**
 * Get feature flag configuration
 * @param flagName - The name of the feature flag
 * @returns The configuration object or undefined if not found
 */
export function getFeatureFlagConfig(flagName: string): FeatureFlagConfig | undefined {
  return defaultFeatureFlags[flagName];
}

/**
 * Register a new feature flag configuration
 * @param config - The feature flag configuration
 */
export function registerFeatureFlag(config: FeatureFlagConfig): void {
  defaultFeatureFlags[config.name] = config;
}

/**
 * Unregister a feature flag configuration
 * @param flagName - The name of the feature flag to unregister
 */
export function unregisterFeatureFlag(flagName: string): void {
  delete defaultFeatureFlags[flagName];
}

/**
 * Type-safe feature flag names
 */
export type FeatureFlagName = keyof typeof defaultFeatureFlags;

/**
 * Type-safe feature flag checker
 * @param flagName - The name of the feature flag
 * @returns The resolved boolean value of the feature flag
 */
export function isFeatureEnabled(flagName: FeatureFlagName): boolean {
  return getFeatureFlag(flagName);
}

/**
 * Environment-specific feature flag checker
 * @param flagName - The name of the feature flag
 * @param environment - The environment to check for
 * @returns true if the flag is enabled for the specified environment
 */
export function isFeatureEnabledForEnvironment(
  flagName: string,
  environment: Environment,
): boolean {
  const config = defaultFeatureFlags[flagName];
  if (!config) {
    return false;
  }

  // Check runtime override first
  if (runtimeOverrides.has(flagName)) {
    return runtimeOverrides.get(flagName)!;
  }

  // Check environment-specific override
  if (config.environmentOverrides && environment in config.environmentOverrides) {
    return config.environmentOverrides[environment]!;
  }

  return config.defaultValue;
}
